import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

export type EnemyVisualPart = {
  mesh: THREE.InstancedMesh;
  motion: 'rigid' | 'growth' | 'rotor';
};

export type EnemyVisualKit = {
  group: THREE.Group;
  roles: EnemyVisualPart[][];
  dispose: () => void;
};

type PartDefinition = {
  name: string;
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
  motion?: EnemyVisualPart['motion'];
  castsShadow?: boolean;
};

function transformGeometry(
  geometry: THREE.BufferGeometry,
  position: [number, number, number] = [0, 0, 0],
  rotation: [number, number, number] = [0, 0, 0],
  scale: [number, number, number] = [1, 1, 1],
): THREE.BufferGeometry {
  geometry.applyMatrix4(
    new THREE.Matrix4().compose(
      new THREE.Vector3(...position),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(...rotation)),
      new THREE.Vector3(...scale),
    ),
  );
  return geometry;
}

function merge(name: string, geometries: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const compatible = geometries.map((source) =>
    source.index ? source.toNonIndexed() : source,
  );
  const geometry = mergeGeometries(compatible, false);
  geometries.forEach((source) => source.dispose());
  compatible.forEach((source, index) => {
    if (source !== geometries[index]) source.dispose();
  });
  if (!geometry) throw new Error(`Unable to merge enemy geometry: ${name}`);
  geometry.name = name;
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
}

function normalizeMergeAttributes(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  const allowed = new Set(['position', 'normal', 'uv']);
  Object.keys(geometry.attributes).forEach((attribute) => {
    if (!allowed.has(attribute)) geometry.deleteAttribute(attribute);
  });
  if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();
  if (!geometry.getAttribute('uv')) {
    const vertices = geometry.getAttribute('position').count;
    geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(vertices * 2), 2));
  }
  return geometry;
}

function extrudedPanel(
  points: Array<[number, number]>,
  depth: number,
  bevel = 0.035,
): THREE.ExtrudeGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(points[0][0], points[0][1]);
  for (let index = 1; index < points.length; index += 1) {
    shape.lineTo(points[index][0], points[index][1]);
  }
  shape.closePath();
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: true,
    bevelSegments: 1,
    bevelSize: bevel,
    bevelThickness: bevel,
    curveSegments: 1,
  });
  geometry.center();
  return geometry;
}

function makeTick(materials: MaterialKit): PartDefinition[] {
  const arms: THREE.BufferGeometry[] = [];
  const rotors: THREE.BufferGeometry[] = [];
  const blades: THREE.BufferGeometry[] = [];
  const growth: THREE.BufferGeometry[] = [];
  for (const x of [-0.68, 0.68]) {
    for (const y of [-0.64, 0.64]) {
      const angle = Math.atan2(y, x);
      arms.push(
        transformGeometry(
          new THREE.BoxGeometry(0.82, 0.11, 0.11),
          [x * 0.53, y * 0.53, 0],
          [0, 0, angle],
        ),
      );
      rotors.push(
        transformGeometry(
          new THREE.TorusGeometry(0.34, 0.055, 7, 18),
          [x, y, 0.12],
        ),
      );
      blades.push(
        transformGeometry(
          new THREE.BoxGeometry(0.58, 0.045, 0.025),
          [x, y, 0.13],
          [0, 0, angle + Math.PI / 4],
        ),
      );
      growth.push(
        transformGeometry(
          new THREE.BoxGeometry(0.38, 0.035, 0.035),
          [x * 0.52, y * 0.52, 0.11],
          [0, 0, angle],
        ),
      );
    }
  }

  return [
    {
      name: 'TICK_FacetedHull',
      geometry: transformGeometry(
        new THREE.DodecahedronGeometry(0.52, 0),
        [0, 0, 0.04],
        [0, 0.35, 0],
        [0.78, 1, 0.52],
      ),
      material: materials.bodyPrimary,
    },
    {
      name: 'TICK_Armature',
      geometry: merge('TICK_Armature', arms),
      material: materials.bodySecondary,
    },
    {
      name: 'TICK_RotorRings',
      geometry: merge('TICK_RotorRings', rotors),
      material: materials.rotor,
      motion: 'rotor',
      castsShadow: false,
    },
    {
      name: 'TICK_RotorBlades',
      geometry: merge('TICK_RotorBlades', blades),
      material: materials.weapon,
      motion: 'rotor',
      castsShadow: false,
    },
    {
      name: 'TICK_OpticsBall',
      geometry: transformGeometry(
        new THREE.SphereGeometry(0.19, 12, 8),
        [0, -0.47, -0.05],
        [Math.PI / 2, 0, 0],
        [1, 0.8, 1],
      ),
      material: materials.lens,
    },
    {
      name: 'TICK_GrowthVeins',
      geometry: merge('TICK_GrowthVeins', growth),
      material: materials.growth,
      motion: 'growth',
      castsShadow: false,
    },
    {
      name: 'TICK_Roundel',
      geometry: transformGeometry(
        new THREE.CircleGeometry(0.17, 18),
        [0, 0.17, 0.48],
      ),
      material: materials.roundel,
      castsShadow: false,
    },
  ];
}

function makePicket(materials: MaterialKit): PartDefinition[] {
  const wing = extrudedPanel(
    [
      [0, 0.18],
      [1.02, -0.35],
      [0.4, -0.28],
      [0.18, -0.58],
      [0, -0.48],
      [-0.18, -0.58],
      [-0.4, -0.28],
      [-1.02, -0.35],
    ],
    0.12,
    0.025,
  );
  const fins = merge('PICKET_ControlFins', [
    transformGeometry(new THREE.BoxGeometry(0.08, 0.48, 0.32), [-0.22, -0.78, 0.18], [0.08, 0, -0.28]),
    transformGeometry(new THREE.BoxGeometry(0.08, 0.48, 0.32), [0.22, -0.78, 0.18], [0.08, 0, 0.28]),
  ]);
  const growth = merge('PICKET_GrowthSpine', [
    transformGeometry(new THREE.BoxGeometry(0.045, 1.15, 0.04), [0, -0.04, 0.3]),
    transformGeometry(new THREE.OctahedronGeometry(0.09, 0), [0, 0.33, 0.34], [0, 0, 0.3]),
    transformGeometry(new THREE.OctahedronGeometry(0.075, 0), [0, -0.08, 0.34], [0, 0, -0.3]),
    transformGeometry(new THREE.OctahedronGeometry(0.06, 0), [0, -0.43, 0.32]),
  ]);

  return [
    {
      name: 'PICKET_Fuselage',
      geometry: transformGeometry(
        new THREE.CapsuleGeometry(0.23, 1.05, 4, 10),
        [0, -0.03, 0.08],
        [0, 0, 0],
        [0.9, 1, 0.72],
      ),
      material: materials.bodyPrimary,
    },
    {
      name: 'PICKET_SweptWing',
      geometry: transformGeometry(wing, [0, -0.1, 0]),
      material: materials.bodySecondary,
    },
    {
      name: 'PICKET_Nose',
      geometry: transformGeometry(
        new THREE.ConeGeometry(0.24, 0.62, 10),
        [0, 0.89, 0.08],
      ),
      material: materials.trim,
    },
    {
      name: 'PICKET_ControlFins',
      geometry: fins,
      material: materials.bodySecondary,
    },
    {
      name: 'PICKET_TargetLens',
      geometry: transformGeometry(
        new THREE.SphereGeometry(0.14, 12, 8),
        [0, 0.57, 0.25],
        [0, 0, 0],
        [1, 1.25, 0.55],
      ),
      material: materials.lens,
    },
    {
      name: 'PICKET_GrowthSpine',
      geometry: growth,
      material: materials.growth,
      motion: 'growth',
      castsShadow: false,
    },
    {
      name: 'PICKET_WarheadBand',
      geometry: transformGeometry(
        new THREE.TorusGeometry(0.22, 0.045, 7, 16),
        [0, 0.56, 0.08],
        [Math.PI / 2, 0, 0],
      ),
      material: materials.hazard,
      castsShadow: false,
    },
  ];
}

function makeRoundel(materials: MaterialKit): PartDefinition[] {
  const wing = extrudedPanel(
    [
      [0, 1.02],
      [0.42, 0.38],
      [1.6, -0.52],
      [0.64, -0.32],
      [0.38, -0.86],
      [0, -0.62],
      [-0.38, -0.86],
      [-0.64, -0.32],
      [-1.6, -0.52],
      [-0.42, 0.38],
    ],
    0.18,
    0.04,
  );
  const panel = extrudedPanel(
    [
      [0, 0.82],
      [0.28, 0.25],
      [0.72, -0.31],
      [0.28, -0.18],
      [0, -0.48],
      [-0.28, -0.18],
      [-0.72, -0.31],
      [-0.28, 0.25],
    ],
    0.08,
    0.02,
  );
  const stores = merge('ROUNDEL_UnderslungStores', [
    transformGeometry(new THREE.CapsuleGeometry(0.09, 0.72, 3, 8), [-0.72, -0.14, -0.18]),
    transformGeometry(new THREE.CapsuleGeometry(0.09, 0.72, 3, 8), [0.72, -0.14, -0.18]),
    transformGeometry(new THREE.ConeGeometry(0.1, 0.25, 8), [-0.72, 0.39, -0.18]),
    transformGeometry(new THREE.ConeGeometry(0.1, 0.25, 8), [0.72, 0.39, -0.18]),
  ]);
  const exhausts = merge('ROUNDEL_Exhausts', [
    transformGeometry(new THREE.TorusGeometry(0.16, 0.045, 8, 16), [-0.34, -0.68, 0.05], [Math.PI / 2, 0, 0]),
    transformGeometry(new THREE.TorusGeometry(0.16, 0.045, 8, 16), [0.34, -0.68, 0.05], [Math.PI / 2, 0, 0]),
  ]);
  const roundels = merge('ROUNDEL_FadedInsignia', [
    transformGeometry(new THREE.CircleGeometry(0.19, 18), [-0.82, -0.2, 0.16]),
    transformGeometry(new THREE.CircleGeometry(0.19, 18), [0.82, -0.2, 0.16]),
  ]);
  const growth = merge('ROUNDEL_GrowthLattice', [
    transformGeometry(new THREE.BoxGeometry(0.05, 1.25, 0.035), [0, 0.02, 0.24]),
    transformGeometry(new THREE.BoxGeometry(0.62, 0.04, 0.035), [-0.42, -0.1, 0.23], [0, 0, -0.55]),
    transformGeometry(new THREE.BoxGeometry(0.62, 0.04, 0.035), [0.42, -0.1, 0.23], [0, 0, 0.55]),
    transformGeometry(new THREE.OctahedronGeometry(0.1, 0), [0, 0.38, 0.28]),
  ]);

  return [
    {
      name: 'ROUNDEL_FlyingWing',
      geometry: wing,
      material: materials.bodyPrimary,
    },
    {
      name: 'ROUNDEL_DorsalPanel',
      geometry: transformGeometry(panel, [0, 0.04, 0.19]),
      material: materials.bodySecondary,
    },
    {
      name: 'ROUNDEL_Canopy',
      geometry: transformGeometry(
        new THREE.SphereGeometry(0.24, 14, 9),
        [0, 0.33, 0.28],
        [0, 0, 0],
        [0.68, 1.3, 0.58],
      ),
      material: materials.lens,
    },
    {
      name: 'ROUNDEL_UnderslungStores',
      geometry: stores,
      material: materials.weapon,
    },
    {
      name: 'ROUNDEL_Exhausts',
      geometry: exhausts,
      material: materials.hazard,
      castsShadow: false,
    },
    {
      name: 'ROUNDEL_FadedInsignia',
      geometry: roundels,
      material: materials.roundel,
      castsShadow: false,
    },
    {
      name: 'ROUNDEL_GrowthLattice',
      geometry: growth,
      material: materials.growth,
      motion: 'growth',
      castsShadow: false,
    },
  ];
}

type MaterialKit = ReturnType<typeof createMaterials>;

function createInheritedSurfaceTexture(): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas 2D unavailable for enemy surface forging.');

  context.fillStyle = '#777777';
  context.fillRect(0, 0, size, size);

  const gradient = context.createLinearGradient(0, 0, size, size);
  gradient.addColorStop(0, 'rgba(255,255,255,0.22)');
  gradient.addColorStop(0.5, 'rgba(32,32,32,0.08)');
  gradient.addColorStop(1, 'rgba(255,255,255,0.12)');
  context.fillStyle = gradient;
  context.fillRect(0, 0, size, size);

  context.strokeStyle = 'rgba(25,25,25,0.5)';
  context.lineWidth = 3;
  for (let position = 0; position <= size; position += 64) {
    context.beginPath();
    context.moveTo(position, 0);
    context.lineTo(position, size);
    context.stroke();
    context.beginPath();
    context.moveTo(0, position);
    context.lineTo(size, position);
    context.stroke();
  }

  let seed = 0x51f15e;
  const random = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0xffffffff;
  };
  for (let index = 0; index < 380; index += 1) {
    const value = 65 + Math.floor(random() * 100);
    context.fillStyle = `rgba(${value},${value},${value},${0.08 + random() * 0.18})`;
    const x = random() * size;
    const y = random() * size;
    context.fillRect(x, y, 0.6 + random() * 2.4, 0.6 + random() * 1.2);
  }
  context.strokeStyle = 'rgba(225,225,215,0.32)';
  context.lineWidth = 1;
  for (let index = 0; index < 24; index += 1) {
    const x = random() * size;
    const y = random() * size;
    context.beginPath();
    context.moveTo(x, y);
    context.lineTo(x + 8 + random() * 26, y - 2 + random() * 4);
    context.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.name = 'InheritedDroneForgedSurface';
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(2.4, 2.4);
  texture.anisotropy = 4;
  texture.needsUpdate = true;
  return texture;
}

function createMaterials(surfaceDetail: THREE.Texture) {
  return {
    bodyPrimary: new THREE.MeshStandardMaterial({
      name: 'InheritedBodyPrimary',
      color: '#687069',
      roughness: 0.48,
      metalness: 0.5,
      bumpMap: surfaceDetail,
      bumpScale: 0.055,
    }),
    bodySecondary: new THREE.MeshStandardMaterial({
      name: 'InheritedBodySecondary',
      color: '#2c3433',
      roughness: 0.64,
      metalness: 0.32,
      bumpMap: surfaceDetail,
      bumpScale: 0.07,
    }),
    trim: new THREE.MeshStandardMaterial({
      name: 'InheritedTrim',
      color: '#b6a37a',
      roughness: 0.42,
      metalness: 0.58,
      bumpMap: surfaceDetail,
      bumpScale: 0.04,
    }),
    hazard: new THREE.MeshStandardMaterial({
      name: 'InheritedHazard',
      color: '#9d2c29',
      emissive: '#3a0808',
      emissiveIntensity: 0.75,
      roughness: 0.42,
      metalness: 0.38,
    }),
    lens: new THREE.MeshPhysicalMaterial({
      name: 'InheritedOptics',
      color: '#111823',
      emissive: '#2fd8ff',
      emissiveIntensity: 1.5,
      roughness: 0.08,
      metalness: 0.25,
      clearcoat: 1,
      clearcoatRoughness: 0.05,
    }),
    growth: new THREE.MeshStandardMaterial({
      name: 'InheritedGrowth',
      color: '#08222b',
      emissive: '#57e8ff',
      emissiveIntensity: 2.1,
      roughness: 0.22,
      metalness: 0.34,
    }),
    weapon: new THREE.MeshStandardMaterial({
      name: 'InheritedWeapon',
      color: '#161b1e',
      roughness: 0.3,
      metalness: 0.86,
    }),
    rotor: new THREE.MeshStandardMaterial({
      name: 'InheritedRotor',
      color: '#6b7374',
      transparent: true,
      opacity: 0.52,
      roughness: 0.25,
      metalness: 0.72,
      depthWrite: false,
    }),
    roundel: new THREE.MeshStandardMaterial({
      name: 'InheritedFadedRoundel',
      color: '#b53a34',
      emissive: '#2c0505',
      emissiveIntensity: 0.35,
      roughness: 0.75,
      metalness: 0.08,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    }),
  };
}

export function createEnemyVisualKit(maxInstances: number): EnemyVisualKit {
  const group = new THREE.Group();
  group.name = 'InheritedDroneModelKit';
  const surfaceDetail = createInheritedSurfaceTexture();
  const materials = createMaterials(surfaceDetail);
  // Ten role slots. The procedural builds are fallbacks; each is replaced by
  // its forged GLB as soon as that loads (see PREMIUM_DRONES).
  const definitions = [
    makeTick(materials),
    makePicket(materials),
    makeRoundel(materials),
    makeTick(materials),      // SPLINTER
    makeRoundel(materials),   // DRAYMAN
    makePicket(materials),    // SHRIKE
    makeTick(materials),      // CROSSTALK
    makeRoundel(materials),   // KESTREL
    makeRoundel(materials),   // GUARD
    makeTick(materials),      // GUN POD
  ];
  const geometries: THREE.BufferGeometry[] = [];
  const roles = definitions.map((role, roleIndex) =>
    role.map((part) => {
      geometries.push(part.geometry);
      const mesh = new THREE.InstancedMesh(
        part.geometry,
        part.material,
        maxInstances,
      );
      mesh.name = part.name;
      mesh.frustumCulled = false;
      mesh.castShadow = part.castsShadow ?? true;
      mesh.receiveShadow = false;
      mesh.renderOrder = 10 + roleIndex;
      group.add(mesh);
      return {
        mesh,
        motion: part.motion ?? 'rigid',
      };
    }),
  );
  if (window.matchMedia('(min-width: 760px)').matches) {
    // Promote every Inherited role to its forged GLB. Each is built by
    // scripts/build-models.mjs against the exact per-unit triangle and
    // footprint spec in bible 05-BESTIARY, with named components so rotors,
    // sensors, and roundels survive the merge as separate material batches.
    for (const spec of PREMIUM_DRONES) {
      void promoteRoleToGlb(spec, group, roles, geometries, materials, maxInstances);
    }
  }

  return {
    group,
    roles,
    dispose: () => {
      geometries.forEach((geometry) => geometry.dispose());
      Object.values(materials).forEach((material) => material.dispose());
      surfaceDetail.dispose();
    },
  };
}

/**
 * Forged GLBs, one per Inherited role. `footprint` is the world-space width the
 * unit must occupy on screen, quoted from bible 05-BESTIARY; the loader scales
 * the mesh to hit it exactly so gameplay reads match the spec regardless of how
 * the source model was authored.
 */
const PREMIUM_DRONES: PremiumDroneSpec[] = [
  { roleIndex: 0, id: 'TICK', url: '/models/thunderhead/drn01-tick.glb', footprint: 1.2 },
  { roleIndex: 1, id: 'PICKET', url: '/models/thunderhead/drn02-picket.glb', footprint: 1.5 },
  { roleIndex: 2, id: 'ROUNDEL', url: '/models/thunderhead/drn03-roundel.glb', footprint: 2.4 },
  { roleIndex: 3, id: 'SPLINTER', url: '/models/thunderhead/drn04-splinter.glb', footprint: 0.9 },
  { roleIndex: 4, id: 'DRAYMAN', url: '/models/thunderhead/drn05-drayman.glb', footprint: 4.6 },
  { roleIndex: 5, id: 'SHRIKE', url: '/models/thunderhead/drn06-shrike.glb', footprint: 1.8 },
  { roleIndex: 6, id: 'CROSSTALK', url: '/models/thunderhead/drn07-crosstalk.glb', footprint: 1.6 },
  { roleIndex: 7, id: 'KESTREL', url: '/models/thunderhead/air01-kestrel.glb', footprint: 2.6 },
  { roleIndex: 8, id: 'GUARD', url: '/models/thunderhead/gnd01-guard.glb', footprint: 3.0 },
  { roleIndex: 9, id: 'GUNPOD', url: '/models/thunderhead/sta01-gunpod.glb', footprint: 2.4 },
];

type PremiumDroneSpec = {
  roleIndex: number;
  id: string;
  url: string;
  footprint: number;
};

async function promoteRoleToGlb(
  spec: PremiumDroneSpec,
  group: THREE.Group,
  roles: EnemyVisualPart[][],
  geometries: THREE.BufferGeometry[],
  materials: MaterialKit,
  maxInstances: number,
): Promise<void> {
  try {
    const gltf = await new GLTFLoader().loadAsync(spec.url);
    const model = gltf.scene;
    model.rotation.x = Math.PI / 2;
    model.updateMatrixWorld(true);

    const initialSize = new THREE.Box3()
      .setFromObject(model)
      .getSize(new THREE.Vector3());
    const scale = spec.footprint / Math.max(initialSize.x, initialSize.y);
    model.scale.setScalar(scale);
    model.updateMatrixWorld(true);
    const center = new THREE.Box3()
      .setFromObject(model)
      .getCenter(new THREE.Vector3());
    model.position.sub(center);
    model.updateMatrixWorld(true);

    const batches = new Map<keyof MaterialKit, THREE.BufferGeometry[]>();
    const categoryFor = (name: string): keyof MaterialKit => {
      // Forged Inherited palette (scripts/model-forge/materials.mjs)
      if (name === 'SensorGlass' || name === 'HostileGlow' || name === 'StatusLamp') return 'lens';
      if (name === 'RoundelBlue' || name === 'RoundelWhite') return 'trim';
      if (name === 'RotorDisc') return 'rotor';
      if (name === 'Rubber' || name === 'HullPanel') return 'bodySecondary';
      if (name === 'Gunmetal') return 'bodySecondary';
      // Legacy hunter-drone palette
      if (name === 'SensorGlow') return 'lens';
      if (name === 'DroneCopper') return 'trim';
      if (name === 'RotorBlade') return 'rotor';
      if (name === 'DroneRubber') return 'bodySecondary';
      return 'bodyPrimary';
    };

    model.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const sourceMaterial = Array.isArray(child.material)
        ? child.material[0]
        : child.material;
      const category = categoryFor(sourceMaterial.name);
      const geometry = normalizeMergeAttributes(child.geometry.clone());
      geometry.applyMatrix4(child.matrixWorld);
      const batch = batches.get(category) ?? [];
      batch.push(geometry);
      batches.set(category, batch);
    });

    const premiumParts: EnemyVisualPart[] = [];
    batches.forEach((batch, category) => {
      const geometry = merge(`${spec.id}_${category}`, batch);
      geometries.push(geometry);
      const mesh = new THREE.InstancedMesh(
        geometry,
        materials[category],
        maxInstances,
      );
      mesh.name = `${spec.id}_${category}`;
      mesh.frustumCulled = false;
      mesh.castShadow = category !== 'rotor';
      mesh.renderOrder = 10 + spec.roleIndex;
      group.add(mesh);
      premiumParts.push({
        mesh,
        motion: category === 'lens' ? 'growth' : 'rigid',
      });
    });

    if (premiumParts.length > 0) {
      const target = roles[spec.roleIndex];
      const retainedGrowth = target.filter((part) => part.motion === 'growth');
      target.forEach((part) => {
        if (part.motion === 'growth') return;
        part.mesh.count = 0;
        part.mesh.removeFromParent();
      });
      roles[spec.roleIndex] = [...premiumParts, ...retainedGrowth];
    }

    const sourceGeometries = new Set<THREE.BufferGeometry>();
    const sourceMaterials = new Set<THREE.Material>();
    model.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      sourceGeometries.add(child.geometry);
      const childMaterials = Array.isArray(child.material)
        ? child.material
        : [child.material];
      childMaterials.forEach((material) => sourceMaterials.add(material));
    });
    sourceGeometries.forEach((geometry) => geometry.dispose());
    sourceMaterials.forEach((material) => material.dispose());
  } catch (error) {
    console.warn(
      `Forged GLB unavailable for ${spec.id}; retaining the procedural build.`,
      error,
    );
  }
}
